import getElementSelectors from '../../shares/lib/getElementSelectors';
import playGame from '../playGames';
import { createIframe } from '../../shares/ui/createIframe';
import eventEmitters from '../../shares/lib/eventEmitters';
import eventType from '../../shares/enums/eventType';
import { applyGameGradient } from '../applyGameGradient';
import { swipeMenusV2 } from '../../shares/ui/swipeMenuV2';
import { isNewMenuEnabled } from '../../shares/lib/isNewSwipeDesign';

export interface ScreenProps {
  clientHeight: number;
  clientWidth: number;
}

export class SwipeCommonMethods {
  /**
   * Apply position-based classes to swipe control elements
   */
  static applyPositionClasses(iconPosition: string): void {
    if (iconPosition === 'right') {
      const swipeControlWrapper = document.querySelector(
        '.swipe-control-wrapper',
      );
      const arrowWrapper = document.querySelector(
        '.swipe-control__arrow-wrapper',
      );
      const arrowRotate = document.querySelector(
        '.swipe-control__arrow-rotate',
      );

      swipeControlWrapper?.classList.add('right-swipe-control-position');
      arrowWrapper?.classList.add('right-arrow-position');
      arrowRotate?.classList.add('arrow-rotate-right');
    }
  }

  /**
   * Restart arrow animation
   */
  static restartArrowAnimation(
    swipe_control: HTMLElement | null,
    animationName: string = '',
  ): void {
    if (!swipe_control) return;

    const arrowWrapper = swipe_control.querySelector(
      '.swipe-control__arrow-wrapper',
    ) as HTMLElement;
    if (arrowWrapper) {
      arrowWrapper.style.animation = 'none';
      arrowWrapper.style.opacity = '1';
      // Force reflow to restart animation
      // eslint-disable-next-line @typescript-eslint/no-unused-expressions
      arrowWrapper.offsetWidth;
      arrowWrapper.style.animation = animationName;
    }
  }

  /**
   * Get icon layers from DOM
   */
  static getIconLayers() {
    const {
      bgLayer,
      gradientLayer,
      swipeIconClose,
      swipeIconOpen,
      swipeControlWrapper,
    } = getElementSelectors();
    return {
      bgLayer,
      gradientLayer,
      swipeIconClose,
      swipeIconOpen,
      swipeControlWrapper,
    };
  }

  /**
   * Calculate swipe animation transformations
   */
  static calculateSwipeAnimation(
    swipe_control: HTMLElement | null,
    swipeHandle: HTMLElement | null,
    x: number,
    diffY: number,
    maxTranslate: number,
    iconPositionClass: string,
  ): void {
    if (!swipe_control) return;

    const isIconPositionClass =
      swipeHandle?.classList.contains(iconPositionClass);
    const swipeProgress = Math.min((Math.abs(x) / maxTranslate) * 100, 100);
    const baseWidth = 54;
    const baseHeight = 70;
    const additionalWidth = Math.abs(x);
    const newWidth = baseWidth + additionalWidth;
    const heightIncrease = additionalWidth * (0.5 + (swipeProgress / 100) * 2);
    const newHeight = baseHeight + heightIncrease;
    const verticalOffset = (newHeight - baseHeight) / 2;
    const bowEffect =
      swipeProgress < 100 ? Math.sin((swipeProgress / 100) * Math.PI) * 20 : 0;

    swipe_control.style.transitionDuration = '';
    swipe_control.style.transitionProperty = '';
    swipe_control.style.transitionTimingFunction = '';

    const translateX = isIconPositionClass ? 0 : x;
    const translateY = diffY - bowEffect - verticalOffset;
    swipe_control.style.transform = `translate(${translateX}px, ${translateY}px)`;
    swipe_control.style.width = `${newWidth}px`;
    swipe_control.style.height = `${newHeight}px`;
    swipe_control.style.clipPath = '';
    (swipe_control.style as any).webkitClipPath = '';

    const { swipeIconClose, swipeIconOpen } = this.getIconLayers();
    const iconScale = 1 + (Math.abs(x) / maxTranslate) * 5;
    if (swipeIconClose) {
      swipeIconClose.style.transform = `scale(${iconScale})`;
    }
    if (swipeIconOpen) {
      swipeIconOpen.style.transform = `scale(${iconScale})`;
    }
  }

  /**
   * Toggle icon visibility
   */
  static toggleIconVisibility(isOpen: boolean): void {
    const { gradientLayer, bgLayer, swipeIconClose, swipeIconOpen } =
      this.getIconLayers();

    if (isOpen) {
      gradientLayer.classList.add('right-icon-swipe-sdk');
      bgLayer.classList.add('right-icon-swipe-sdk');
      gradientLayer.classList.add('right-icon-gradient-layer');
      swipeIconClose.classList.remove('__swipe-swipe-hide__');
      swipeIconOpen.classList.add('__swipe-swipe-hide__');
    } else {
      gradientLayer.classList.remove('right-icon-swipe-sdk');
      bgLayer.classList.remove('right-icon-swipe-sdk');
      gradientLayer.classList.remove('right-icon-gradient-layer');
      swipeIconClose.classList.add('__swipe-swipe-hide__');
      swipeIconOpen.classList.remove('__swipe-swipe-hide__');
    }
  }

  /**
   * Right swipe animation
   */
  static rightSwipeAnimation(): void {
    const { gradientLayer } = this.getIconLayers();
    const isCurrentlyOpen = gradientLayer.classList.contains(
      'right-icon-swipe-sdk',
    );
    this.toggleIconVisibility(!isCurrentlyOpen);
  }

  /**
   * Set layer opacity
   */
  static setLayerOpacity(opacity: string, transition: string): void {
    const { bgLayer, gradientLayer } = this.getIconLayers();

    if (bgLayer) {
      bgLayer.style.opacity = opacity;
      bgLayer.style.transition = transition;
    }
    if (gradientLayer) {
      gradientLayer.style.opacity = opacity;
      gradientLayer.style.transition = transition;
    }
  }

  /**
   * Reset styles and icon layers
   */
  static resetStylesAndIconLayers(swipe_control: HTMLElement | null): void {
    if (!swipe_control) return;

    const styleProps = ['width', 'height', 'clipPath', 'left'];
    for (const prop of styleProps) {
      swipe_control.style[prop as any] = '';
    }
    (swipe_control.style as any).webkitClipPath = '';

    this.setLayerOpacity('1', '');

    const { swipeIconClose, swipeIconOpen } = this.getIconLayers();
    if (swipeIconClose) {
      swipeIconClose.style.transform = '';
      swipeIconClose.style.transition = '';
    }
    if (swipeIconOpen) {
      swipeIconOpen.style.transform = '';
      swipeIconOpen.style.transition = '';
    }
  }

  /**
   * Get transform values from transform string
   */
  static getTransformValues(transform: string): {
    translateX: number;
    translateY: number;
  } {
    let translateX = 0;
    let translateY = 0;

    if (transform && transform !== 'none') {
      const regex = /matrix[^(]*\(([^)]+)\)/;
      const matrix = regex.exec(transform);
      if (matrix?.[1]) {
        const values = matrix[1].split(',').map((v) => v.trim());
        translateX = Number.parseFloat(values[4] || '0');
        translateY = Number.parseFloat(values[5] || '0');
      }
    }

    return { translateX, translateY };
  }

  /**
   * Calculate final top position
   */
  static calculateFinalTop(rect: DOMRect): number {
    const viewportHeight = globalThis.innerHeight;
    const currentCenterY = rect.top + rect.height / 2;
    let finalTop = currentCenterY - viewportHeight / 2;

    if (finalTop < 0 || finalTop + viewportHeight > viewportHeight) {
      finalTop = 0;
    }

    return Math.max(0, Math.min(finalTop, 0));
  }

  /**
   * Prepare control for animation
   */
  static prepareControlForAnimation(
    swipe_control: HTMLElement | null,
    currentActualTop: number,
    currentWidth: number,
    currentHeight: number,
  ): void {
    if (!swipe_control) return;

    swipe_control.style.transition = 'none';
    swipe_control.style.top = `${currentActualTop}px`;
    swipe_control.style.transform = 'none';
    swipe_control.style.bottom = 'auto';
    swipe_control.style.height = `${currentHeight}px`;
    swipe_control.style.width = `${currentWidth}px`;

    // eslint-disable-next-line no-unused-expressions, @typescript-eslint/no-unused-expressions -- reading offsetHeight forces reflow
    // NOSONAR -- suppress SonarCloud warning for "expected an assignment or function call"
    swipe_control.offsetHeight; // NOSONAR
  }

  /**
   * Apply expand transition
   */
  static applyExpandTransition(
    swipe_control: HTMLElement | null,
    finalTop: number,
    positionProp: 'left' | 'right',
    positionValue: string,
    width: string = '100vw',
    transitionDuration: string = '0.3s',
  ): void {
    if (!swipe_control) return;

    swipe_control.style.transitionDuration = transitionDuration;
    swipe_control.style.transitionProperty = `width, height, ${positionProp}, top`;
    swipe_control.style.transitionTimingFunction = 'ease-out';

    swipe_control.style.top = `${finalTop}px`;
    swipe_control.style[positionProp] = positionValue;
    swipe_control.style.width = width;
    swipe_control.style.height = '100vh';

    if (positionProp === 'left') {
      swipe_control.style.right = '';
      swipe_control.style.clipPath = 'none';
      (swipe_control.style as any).webkitClipPath = 'none';
    }
  }

  /**
   * Animate arrow end
   */
  static animateArrowEnd(rightValue: string): void {
    const arrowEnd = document.querySelector(
      '.swipe-sdk-arraw-end',
    ) as HTMLElement;

    if (!arrowEnd) return;

    arrowEnd.classList.add('swipe-sdk-arraw-fade-out');
    setTimeout(() => {
      arrowEnd.classList.remove('swipe-sdk-arraw-fade-out');
      arrowEnd.style.right = rightValue;
    }, 500);
  }

  /**
   * Reset swipe position
   */
  static resetSwipePosition(
    swipe_control: HTMLElement | null,
    swipeHandle: HTMLElement | null,
    pretY: number,
    screenProps: ScreenProps,
    isArrowIconsFeature: boolean,
  ): void {
    if (!isArrowIconsFeature && swipeHandle) {
      swipeHandle.style.transitionDuration = '0.2s';
    }
    if (swipeHandle) {
      swipeHandle.style.transform = '';
    }
    if (swipe_control) {
      swipe_control.style.transform = '';
      swipe_control.style.top = pretY >= 0 ? `${pretY}px` : '6px';

      if (isArrowIconsFeature) {
        this.resetStylesAndIconLayers(swipe_control);
      }

      const swipePosYPerc = (pretY / screenProps.clientHeight) * 100;
      sessionStorage.setItem('swipeYPos', swipePosYPerc.toFixed(2));
    }
    setTimeout(() => {
      if (swipeHandle) {
        swipeHandle.style.transitionDuration = '';
      }
    }, 100);
  }

  /**
   * Remove shimmer effect from swipe control wrapper
   */
  static removeShimmerEffect(): void {
    const { swipeControlWrapper } = this.getIconLayers();
    if (swipeControlWrapper) {
      swipeControlWrapper.classList.remove('shimmer-effect-swipe-sdk');
    }
  }

  /**
   * Add shimmer effect to swipe control wrapper
   */
  static addShimmerEffect(): void {
    const { swipeControlWrapper } = this.getIconLayers();
    if (swipeControlWrapper) {
      swipeControlWrapper.classList.add('shimmer-effect-swipe-sdk');
    }
  }

  /**
   * Handle swipe open
   */
  static async handleSwipeOpen(
    swipeHandle: HTMLElement | null,
    swipeSdkContainer: HTMLElement | null,
    swipe_control: HTMLElement | null,
    isArrowIconsFeature: boolean,
  ): Promise<void> {
    const swipeSDKFrame = document.querySelector<HTMLElement>(
      '#swipe-sdk-container #swipeSDKFrame',
    );

    if (isNewMenuEnabled()) {
      swipeMenusV2.loadMenu();
    }

    swipeSDKFrame?.style.setProperty('position', 'fixed');
    swipeSdkContainer?.classList.add('preventing-swipe-scroll');

    // Remove shimmer effect when opening swipe
    this.removeShimmerEffect();

    setTimeout(async () => {
      if (swipeHandle) {
        swipeHandle.style.background = 'none';
      }
      if (!document.getElementById('swipeSDKFrame')) {
        const gameLaunchData = await playGame();
        createIframe(gameLaunchData);
      }
    }, 300);

    if (isArrowIconsFeature && swipe_control) {
      swipe_control.style.right = '0px';
    }

    eventEmitters(eventType.SWIPE_OPEN);
  }

  /**
   * Handle swipe close
   */
  static handleSwipeClose(
    swipeSdkContainer: HTMLElement | null,
    swipe_control: HTMLElement | null,
    isArrowIconsFeature: boolean,
  ): void {
    if (isArrowIconsFeature) {
      setTimeout(() => {
        if (swipe_control) {
          swipe_control.style.right = '-55px';
        }
      }, 300);
    }

    swipeSdkContainer?.classList.remove('preventing-swipe-scroll');

    // Add shimmer effect back when closing swipe
    this.addShimmerEffect();

    const swipeSDKFrame = document.querySelector<HTMLElement>(
      '#swipe-sdk-container #swipeSDKFrame',
    );
    swipeSDKFrame?.style.removeProperty('position');
    setTimeout(() => globalThis.scrollTo(0, 1), 200);
    eventEmitters(eventType.SWIPE_CLOSED);
  }

  /**
   * Handle expand animation
   */
  static handleExpandAnimation(
    swipe_control: HTMLElement | null,
    swipeHandle: HTMLElement | null,
    iconPositionClass: string,
    isIconPositionClass: boolean,
    options: {
      onComplete: () => void;
      opacityTransition?: string;
      width?: string;
      transitionDuration?: string;
    },
  ): void {
    if (!swipe_control) return;

    const {
      onComplete,
      opacityTransition = 'opacity 0.8s ease-out',
      width = '100vw',
      transitionDuration = '0.3s',
    } = options;

    this.setLayerOpacity('0', opacityTransition);

    const rect = swipe_control.getBoundingClientRect();
    const finalTop = this.calculateFinalTop(rect);
    const transform = globalThis.getComputedStyle(swipe_control).transform;
    const { translateY } = this.getTransformValues(transform);
    const currentActualTop = swipe_control.offsetTop + translateY;

    this.prepareControlForAnimation(
      swipe_control,
      currentActualTop,
      rect.width,
      rect.height,
    );

    if (isIconPositionClass) {
      this.applyExpandTransition(
        swipe_control,
        finalTop,
        'right',
        '0px',
        width,
        transitionDuration,
      );
      this.animateArrowEnd('');
    } else {
      this.applyExpandTransition(
        swipe_control,
        finalTop,
        'left',
        '100%',
        width,
        transitionDuration,
      );
      this.animateArrowEnd('-100vw');
    }

    setTimeout(() => {
      swipeHandle?.classList.toggle(iconPositionClass);
      onComplete();
      this.rightSwipeAnimation();
      applyGameGradient(iconPositionClass);
    }, 150);
  }
}
